import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import once from 'lodash/once';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { isForgeCustomFieldsOnPortalViewEnabled } from '@helpcenter/feature-flags';
import type { FieldFormProps, FieldValueType } from '@helpcenter/field-types';
import { showErrorFlag } from '@helpcenter/flags';
import type { ShowErrorFlag } from '@helpcenter/flags';
import { AtlassianHelpConfigAccountType } from '@helpcenter/forge-ui-utils';
import type {
	ExtensionPointModule,
	CustomField,
	CustomFieldType,
	CustomFieldExtensionData,
	CustomFieldExtensionPayload,
	WithBlockedAppsBannerProps,
} from '@helpcenter/forge-ui-utils';
import {
	CUSTOM_FIELD,
	CUSTOM_FIELD_TYPE,
	FORGE_REQUEST_CREATE,
} from '@helpcenter/forge-ui-utils/src/constants';
import { getMetaFromHTML } from '@helpcenter/meta';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import type { State as AppState } from 'state';
import { mapDispatchToPropsBanner } from 'state/persisted/blocked-apps-banner/reducer';
import type { BlockedAppsBannerActions } from 'state/persisted/blocked-apps-banner/reducer';
import { getIssueType, getIssueTypeId } from 'state/selectors/request-create';
import { useShowBanner } from 'utils/blocked-apps-banner';
import { FieldBase } from 'view/common/fields/field-base';
import { useInlineField } from 'view/common/forge-custom-fields/controllers/store';
import { ExtensionTitle } from 'view/common/forge-ui/extension-title';
import { ForgeCustomFieldEdit } from 'view/forge-ui/custom-field';
import {
	getExtensionIdFromLegacyFieldType,
	isForgeLegacyFieldType,
} from 'view/forge-ui/custom-field/utils';
import {
	createAttributes,
	fireOperationalEvent,
	fireScreenEvent,
} from 'view/forge-ui-analytics/common';
import {
	PACKAGE_NAME_GASV3,
	SOURCES,
	EXPERIENCES,
	RENDER_CONTEXTS,
	OPERATIONAL_ACTIONS,
} from 'view/forge-ui-analytics/common/constants';
import {
	createInitializationFinished,
	createInitializationFailed,
} from 'view/forge-ui-analytics/services/fetch-modules';
import { FIELD_STATE_FAILED } from '../../forge-custom-fields/controllers/store/constants';
import { useExtensions } from '../../forge-ui/use-extensions';
import { ErrorMessage } from './error-message';
import { FieldState } from './field-state';
import messages from './messages';
import { Skeleton } from './skeleton';
import { getMappedFieldType, transformValue } from './utils/utils';
import type { FieldType } from 'state/persisted/request-create/types';
import type { AnalyticsAttributes, Source } from 'view/forge-ui-analytics/common/types';
import type { ForgeCustomFieldAnalyticsAttributes } from 'view/common/forge-custom-fields/types';

interface Props extends FieldFormProps<string> {
	value?: FieldValueType;
	extension?: CustomField | CustomFieldType;
	disabled: boolean;
	portalId: number;
	requestTypeId: number;
	projectId?: string;
	onChange: (name: string, value: unknown, notFromUserInteraction?: boolean) => void;
	fallbackRender: (newType: FieldType) => JSX.Element;
	// TODO: remove the ones below with forge_custom_fields_on_jsm_portal_view FG cleanup
	jiraFieldType?: string;
	enableSubmitButton?: () => void;
	disableSubmitButton?: () => void;
}

interface SelectorProvidedProps {
	issueType?: string;
	issueTypeId?: string;
}

interface WithConnectedProps extends Props, SelectorProvidedProps, WithBlockedAppsBannerProps {
	showErrorFlag: typeof showErrorFlag;
}

interface PropsWithAnalyticsAttributes extends WithConnectedProps {
	analyticsAttributes: AnalyticsAttributes;
}

const PORTAL_REQUEST_ANALYTICS_ATTRIBUTES: Readonly<ForgeCustomFieldAnalyticsAttributes> = {
	source: SOURCES.PORTAL_REQUEST,
	renderContext: RENDER_CONTEXTS.PORTAL_REQUEST,
	experience: EXPERIENCES.PORTAL_REQUEST,
};

const ForgeFieldWithExtension: React.FC<
	PropsWithAnalyticsAttributes & {
		extension: CustomField | CustomFieldType;
		loadingComponent: React.ReactNode;
		isVisible: boolean;
	}
> = ({
	id,
	value,
	extension,
	requestTypeId,
	portalId,
	projectId,
	issueType,
	issueTypeId,
	disabled,
	loadingComponent,
	isVisible,
	onChange,
}) => {
	di(ForgeCustomFieldEdit, FieldState);

	const extensionData: CustomFieldExtensionData = useMemo(
		() => ({
			fieldType: extension.id,
			fieldId: id,
			type: extension.type,
			portal: {
				id: portalId,
			},
			request: {
				typeId: requestTypeId,
			},
			renderContext: RENDER_CONTEXTS.PORTAL_REQUEST,
			experience: EXPERIENCES.PORTAL_REQUEST,
			entryPoint: 'edit',
			project: {
				id: projectId,
				type: 'service_desk',
			},
			issue: {
				type: issueType,
				typeId: issueTypeId,
			},
		}),
		[extension.id, extension.type, id, portalId, requestTypeId, projectId, issueType, issueTypeId],
	);

	const extensionPayload: CustomFieldExtensionPayload = useMemo(
		() => ({ fieldValue: value }),
		[value],
	);
	const onSubmit = useCallback(
		(fieldValue: unknown) =>
			onChange(extensionData.fieldId, transformValue(fieldValue, extension), true),
		[extensionData.fieldId, extension, onChange],
	);

	return (
		<FieldState isDisabled={disabled} isVisible={isVisible}>
			<ForgeCustomFieldEdit
				extension={extension}
				extensionData={extensionData}
				extensionPayload={extensionPayload}
				loadingComponent={loadingComponent}
				onSubmit={onSubmit}
				analyticsAttributes={PORTAL_REQUEST_ANALYTICS_ATTRIBUTES}
			/>
		</FieldState>
	);
};

const onInitializationFinished = createInitializationFinished(SOURCES.PORTAL_REQUEST, true);
const onInitializationFailed = createInitializationFailed(SOURCES.PORTAL_REQUEST, true);

const ForgeFieldImplOld = (props: PropsWithAnalyticsAttributes) => {
	di(
		getMetaFromHTML,
		useExtensions,
		FieldBase,
		Skeleton,
		useInlineField,
		fireScreenEvent,
		fireOperationalEvent,
		ErrorMessage,
		useIntl,
	);

	const {
		disableSubmitButton,
		enableSubmitButton,
		fallbackRender,
		jiraFieldType,
		id,
		analyticsAttributes,
	} = props;
	const [isRendered, setIsRendered] = useState(false);
	const accountType =
		getMetaFromHTML('ajs-account-type') || AtlassianHelpConfigAccountType.ANONYMOUS;
	const hasAccess = accountType === AtlassianHelpConfigAccountType.ATLASSIAN;
	const [fieldState] = useInlineField(id);
	const { formatMessage } = useIntl();

	const fireScreenEventOnce = useMemo(() => once(fireScreenEvent), []);
	const fireNotFoundOnce = useMemo(() => once(fireOperationalEvent), []);
	const showNotFoundFlagOnce = useMemo(() => once(props.showErrorFlag), [props.showErrorFlag]);

	const useExtensionsConfig = useMemo(
		() => ({
			onInitializationFinished,
			onInitializationFailed,
			types: [CUSTOM_FIELD, CUSTOM_FIELD_TYPE] as ExtensionPointModule[],
			context: {
				portalId: String(props.portalId),
				requestTypeId: String(props.requestTypeId),
			},
			includeHidden: true,
			shouldFetch: hasAccess,
		}),
		[hasAccess, props.portalId, props.requestTypeId],
	);

	const { extensions, isLoading, error } = useExtensions<CustomField | CustomFieldType>(
		useExtensionsConfig,
	);
	const onRendered = useCallback(() => {
		setIsRendered(true);
		// oxlint-disable-next-line no-void
		void fireScreenEventOnce({
			attributes: { ...analyticsAttributes, entryPoint: 'edit' },
			id: 'customFieldEditScreen',
			source: SOURCES.PORTAL_REQUEST,
		});
	}, [analyticsAttributes, fireScreenEventOnce]);

	useEffect(() => {
		isLoading ? disableSubmitButton?.() : enableSubmitButton?.();
	}, [isLoading, disableSubmitButton, enableSubmitButton]);

	const extension = useMemo(() => {
		const extensionId = isForgeLegacyFieldType(jiraFieldType || '')
			? getExtensionIdFromLegacyFieldType(jiraFieldType || '')
			: jiraFieldType;
		return extensions.find((field) => field.id === extensionId);
	}, [extensions, jiraFieldType]);

	// We are going to pass to the list only the extension that is found.
	// This is to avoid showing the banner for the extensions that are not added to the experience but still installed on the tenant.
	const extensionsToCheck = useMemo(() => (extension ? [extension] : []), [extension]);
	useShowBanner(
		extensionsToCheck,
		FORGE_REQUEST_CREATE,
		hasAccess,
		props.showBanner,
		props.resetBannerState,
	);

	const shouldFallBackToNativeRendering = useMemo(() => {
		// Too early to tell, just assume we have a custom renderer
		if (!extension) return false;
		// There is no resource, so it's not a custom renderer
		if (typeof extension.properties.edit?.resource !== 'string') return true;
		// There must be a correct experience defined, otherwise we'd need to fallback to the native rendering
		const isValidExperience = extension.properties.edit?.experience?.includes(
			EXPERIENCES.PORTAL_REQUEST,
		);
		return !isValidExperience;
	}, [extension]);

	const preferredFieldNode = useMemo(() => {
		if (shouldFallBackToNativeRendering && extension) {
			return fallbackRender(getMappedFieldType(extension));
		}
		return null;
	}, [extension, fallbackRender, shouldFallBackToNativeRendering]);

	const fieldBaseProps = useMemo(
		() => ({
			label: extension ? (
				<ExtensionTitle
					extensionName={props.label}
					extensionEnvType={extension.environmentType}
					extensionEnvKey={extension.environmentKey}
				/>
			) : (
				props.label
			),
			htmlFor: props.id,
			required: props.required,
			description: props.description,
			labelId: `${props.id}-label`,
		}),
		[props.description, props.id, props.label, props.required, extension],
	);

	const mergedErrorMessages = useMemo(
		() =>
			[
				fieldState?.status === FIELD_STATE_FAILED && formatMessage(messages.errorMessage),
				props.errorMessage,
			].filter(Boolean) as string[],
		[fieldState?.status, props.errorMessage, formatMessage],
	);

	const isHidden = typeof extension?.hiddenBy === 'string';

	if (!hasAccess || isHidden || error) {
		return null;
	}

	if (!extension && !isLoading) {
		// oxlint-disable-next-line no-void
		void fireNotFoundOnce(OPERATIONAL_ACTIONS.FAILED, {
			attributes: {
				...analyticsAttributes,
				entryPoint: 'edit',
				reason: 'notFound',
				required: props.required,
			},
			id: 'customFieldEdit',
			source: SOURCES.PORTAL_REQUEST,
		});
		if (props.required) {
			showNotFoundFlagOnce(
				messages.notFoundRequiredFieldTitle,
				messages.notFoundRequiredFieldDescription,
				[],
				undefined,
				{ fieldName: props.label },
			);
		}
		return null;
	}

	if (preferredFieldNode) {
		return preferredFieldNode;
	}

	return (
		<FieldBase
			{...fieldBaseProps}
			errorMessage={<ErrorMessage errorMessages={mergedErrorMessages} />}
		>
			{isLoading || !isRendered ? <Skeleton /> : null}
			{extension && (
				<ForgeFieldWithExtension
					{...props}
					extension={extension}
					loadingComponent={<Skeleton onUnmount={onRendered} />}
					isVisible={isRendered}
				/>
			)}
		</FieldBase>
	);
};

const ForgeFieldImplNew = (props: PropsWithAnalyticsAttributes) => {
	di(
		FieldBase,
		Skeleton,
		useInlineField,
		fireScreenEvent,
		fireOperationalEvent,
		ErrorMessage,
		useIntl,
	);

	const { extension, fallbackRender, id, analyticsAttributes } = props;
	const [isRendered, setIsRendered] = useState(false);
	const [fieldState] = useInlineField(id);
	const { formatMessage } = useIntl();

	const fireScreenEventOnce = useMemo(() => once(fireScreenEvent), []);
	const fireNotFoundOnce = useMemo(() => once(fireOperationalEvent), []);
	const showNotFoundFlagOnce = useMemo(() => once(props.showErrorFlag), [props.showErrorFlag]);

	const onRendered = useCallback(() => {
		setIsRendered(true);
		// oxlint-disable-next-line no-void
		void fireScreenEventOnce({
			attributes: { ...analyticsAttributes, entryPoint: 'edit' },
			id: 'customFieldEditScreen',
			source: SOURCES.PORTAL_REQUEST,
		});
	}, [analyticsAttributes, fireScreenEventOnce]);

	// Transforming the extension to the old format which is used in the blocked apps banner
	const extensionsToCheck = useMemo(
		() =>
			extension
				? [
						{
							...extension,
							dataClassificationPolicyDecision: {
								status: extension.hiddenBy === 'AppAccessRules' ? 'BLOCKED' : 'ALLOWED',
							},
						},
					]
				: [],
		[extension],
	);
	useShowBanner(
		extensionsToCheck,
		FORGE_REQUEST_CREATE,
		true,
		props.showBanner,
		props.resetBannerState,
	);

	const shouldFallBackToNativeRendering = useMemo(() => {
		// No way to determine, so falling back to native rendering
		if (!extension) return false;
		// There is no resource, so it's not a custom renderer
		if (typeof extension.properties.edit?.resource !== 'string') return true;
		// There must be a correct experience defined, otherwise we'd need to fallback to the native rendering
		const isValidExperience = extension.properties.edit?.experience?.includes(
			EXPERIENCES.PORTAL_REQUEST,
		);
		return !isValidExperience;
	}, [extension]);

	const preferredFieldNode = useMemo(() => {
		if (shouldFallBackToNativeRendering && extension) {
			return fallbackRender(getMappedFieldType(extension));
		}
		return null;
	}, [extension, fallbackRender, shouldFallBackToNativeRendering]);

	const fieldBaseProps = useMemo(
		() => ({
			label: extension ? (
				<ExtensionTitle
					extensionName={props.label}
					extensionEnvType={extension.environmentType}
					extensionEnvKey={extension.environmentKey}
				/>
			) : (
				props.label
			),
			htmlFor: props.id,
			required: props.required,
			description: props.description,
			labelId: `${props.id}-label`,
		}),
		[props.description, props.id, props.label, props.required, extension],
	);

	const mergedErrorMessages = useMemo(
		() =>
			[
				fieldState?.status === FIELD_STATE_FAILED && formatMessage(messages.errorMessage),
				props.errorMessage,
			].filter(Boolean) as string[],
		[fieldState?.status, props.errorMessage, formatMessage],
	);

	const errorMessageContent = useMemo(
		() =>
			mergedErrorMessages && mergedErrorMessages.length > 0 ? (
				<ErrorMessage errorMessages={mergedErrorMessages} />
			) : undefined,
		[mergedErrorMessages],
	);

	if (!extension) {
		// oxlint-disable-next-line no-void
		void fireNotFoundOnce(OPERATIONAL_ACTIONS.FAILED, {
			attributes: {
				...analyticsAttributes,
				entryPoint: 'edit',
				reason: 'notFound',
				required: props.required,
			},
			id: 'customFieldEdit',
			source: SOURCES.PORTAL_REQUEST,
		});
		if (props.required) {
			showNotFoundFlagOnce(
				messages.notFoundRequiredFieldTitle,
				messages.notFoundRequiredFieldDescription,
				[],
				undefined,
				{ fieldName: props.label },
			);
		}
		return null;
	}

	const isHidden = typeof extension?.hiddenBy === 'string';
	if (isHidden) {
		return null;
	}

	if (preferredFieldNode) {
		return preferredFieldNode;
	}

	if (__SERVER__) {
		return (
			<FieldBase {...fieldBaseProps}>
				<Skeleton />
			</FieldBase>
		);
	}

	return (
		<FieldBase {...fieldBaseProps} errorMessage={errorMessageContent}>
			{!isRendered ? <Skeleton /> : null}
			<ForgeFieldWithExtension
				{...props}
				extension={extension}
				loadingComponent={<Skeleton onUnmount={onRendered} />}
				isVisible={isRendered}
			/>
		</FieldBase>
	);
};

const ForgeFieldImpl = (props: PropsWithAnalyticsAttributes) => {
	di(isForgeCustomFieldsOnPortalViewEnabled);

	if (isForgeCustomFieldsOnPortalViewEnabled()) {
		return <ForgeFieldImplNew {...props} />;
	}
	return <ForgeFieldImplOld {...props} />;
};

export const ForgeFieldWithErrorBoundary = (props: WithConnectedProps) => {
	di(ForgeFieldImpl, ErrorBoundary, createAttributes);

	const id = 'customFieldEdit';
	const rawAttributes: {
		source: Source;
		renderContext: string;
		experience: string;
	} = useMemo(
		() => ({
			source: SOURCES.PORTAL_REQUEST,
			renderContext: RENDER_CONTEXTS.PORTAL_REQUEST,
			experience: EXPERIENCES.PORTAL_REQUEST,
		}),
		[],
	);
	const analyticsAttributes = useMemo(() => createAttributes(rawAttributes), [rawAttributes]);

	return (
		<ErrorBoundary
			id={id}
			packageName={PACKAGE_NAME_GASV3}
			analyticsAttributes={analyticsAttributes}
		>
			<ForgeFieldImpl {...props} analyticsAttributes={rawAttributes} />
		</ErrorBoundary>
	);
};

const mapDispatchToProps = (dispatch: React.Dispatch<BlockedAppsBannerActions | ShowErrorFlag>) => {
	return {
		...mapDispatchToPropsBanner(dispatch),
		showErrorFlag: (...args: Parameters<typeof showErrorFlag>): void =>
			dispatch(showErrorFlag(...args)),
	};
};

export const ForgeField = connect<
	SelectorProvidedProps,
	ReturnType<typeof mapDispatchToProps>,
	Props,
	AppState
>((state: AppState, ownProps: Props) => {
	return {
		issueType: getIssueType(state, ownProps.portalId, ownProps.requestTypeId),
		issueTypeId: getIssueTypeId(state, ownProps.portalId, ownProps.requestTypeId),
	};
}, mapDispatchToProps)(ForgeFieldWithErrorBoundary);
